import Yggdrasil from 'services/yggdrasil'
import { constructFakeCourseFromCertificate, mapCertificateToFakeCourse } from 'store/shared/certificateHelper'
import { cloneDeep, compact, uniqBy } from 'lodash'

const state = {
    certificates: [],
    learningPathsForCertificates: [],
    reloadLearningPaths: false
}

const mutations = {
    ASSIGN_CERTIFICATES (state, certificates) {
        state.certificates = certificates
    },
    ASSIGN_COURSES_FOR_CERTIFICATES (state, list) {
        state.coursesForCertificates = list
    },
    ASSIGN_LEARNING_PATHS_FOR_CERTIFICATES (state, list) {
        state.learningPathsForCertificates = list
    },
    CLEAR_AFTER_LOGOUT (state) {
        state.certificates = []
        state.coursesForCertificates = []
        state.learningPathsForCertificates = []
    },
    SET_RELOAD_LEARNING_PATHS (state, shouldReload) {
        state.reloadLearningPaths = shouldReload
    }
}

const actions = {
    async clearAfterLogout (store) {
        store.commit('CLEAR_AFTER_LOGOUT')
    },
    async loadCertificates (store) {
        const certificates = await Yggdrasil.loadCertificates()
        certificates.courses = certificates.courses.map(aesirCourseCertificate => {
            const { course: _, ...certificate } = aesirCourseCertificate
            if (!!certificate.certs?.length && !certificate.durations?.length) {
                certificate.durations = certificate.certs.map(cert => ({
                    category: cert,
                    credits: Number((certificate.duration / 60).toFixed(2))
                }))
            }
            certificate.certificationDate = certificate.date

            return {
                ...aesirCourseCertificate.course,
                level: aesirCourseCertificate.level,
                entityType: 'course',
                title: aesirCourseCertificate.courseTitle,
                videoDuration: aesirCourseCertificate.duration,
                description: aesirCourseCertificate.courseDescription,
                certifiedBy: compact(aesirCourseCertificate.certs).map(cert => ({ label: cert, value: cert })),
                certificate: {
                    ...certificate,
                    // override certificate categories with course categories. Should we do that?
                    categories: aesirCourseCertificate.course.categories,
                    courseTypeValue: aesirCourseCertificate.course.type?.value,
                    authorizationId: aesirCourseCertificate.course.authorizationId,
                    entityType: 'certificate'
                }
            }
        })

        certificates.learningPaths = certificates.learningPaths.map(learningPathCertificate => {
            const learningPathCourseItems = certificates.courses.filter(course => learningPathCertificate.courseIds.includes(course.id))
            const learningPathTotalDuration = learningPathCourseItems.reduce((totalDuration, learningPathCourse) => {
                return totalDuration + learningPathCourse.certificate.duration
            }, 0)
            const learningPathInstructors = uniqBy(
                learningPathCourseItems.reduce((instructors, learningPathCourse) => {
                    return instructors.concat(learningPathCourse.instructors)
                }, []),
                'name'
            )
            const learningPathCategories = uniqBy(
                learningPathCourseItems.reduce((categories, learningPathCourse) => {
                    return categories.concat(learningPathCourse.categories)
                }, []),
                'id'
            )

            const { learningPath: _, ...certificateWithoutLearningPath } = learningPathCertificate
            certificateWithoutLearningPath.certificationDate = learningPathCertificate.date
            return {
                ...learningPathCertificate.learningPath,
                percentageCompleted: 100,
                certificate: {
                    ...certificateWithoutLearningPath,
                    categories: learningPathCategories,
                    instructors: learningPathInstructors,
                    courseCertificates: learningPathCourseItems.map(course => course.certificate),
                    certificationDate: learningPathCertificate.certificationDate,
                    duration: learningPathTotalDuration,
                    entityType: 'learningPath'
                },
                id: learningPathCertificate.learningPathId,
                title: learningPathCertificate.learningPathName,
                courses: learningPathCourseItems,
                categories: learningPathCategories,
                duration: learningPathTotalDuration,
                instructors: learningPathInstructors,
                entityType: 'learningPath',
                description: learningPathCertificate.learningPathDescription
            }
        })
        store.commit('ASSIGN_CERTIFICATES', certificates)
    },
    async loadCourseTestUrl (store, courseId) {
        return Yggdrasil.loadCourseTestUrl(courseId)
    },
    async addNoQuizCertificate (store, courseId) {
        return Yggdrasil.addNoQuizCertificate(courseId)
    },
    async setReloadLearningPathsState (store, shouldReload) {
        store.commit('SET_RELOAD_LEARNING_PATHS', shouldReload)
    }
}

const getters = {
    certificates: state => state.certificates,
    isCourseCompleted: (state) => (courseId) => {
        if (state.certificates.courses === undefined || state.certificates.courses.length === 0) return false
        return !!state.certificates.courses.find(course => +course.certificate.courseId === +courseId) ||
            !!state.certificates.learningPaths.find(learningPath => +learningPath.certificate.learningPathId === +courseId)
    },
    getCertificateForCourse: (state) => (courseId) => {
        if (state.certificates.courses === undefined || state.certificates.courses.length === 0) return false
        const course = state.certificates.courses.find(certificate => +certificate.id === +courseId)
        if (course === undefined) return false
        return course.certificate
    },
    getCertificateForLearningPath: (state) => (learningPathId) => {
        const learningPath = state.certificates.learningPaths.find(certificate => +certificate.id === +learningPathId)
        if (learningPath === undefined) return false
        return learningPath.certificate
    },
    shouldReloadLearningPaths: state => state.reloadLearningPaths
}

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters
}
